import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/layout/Navbar';
import PageHeader from '../components/layout/PageHeader';
import GuidedTour from '../components/tour/GuidedTour';
import UnverifiedExplainer from '../components/UnverifiedExplainer';
import {
  ScanLine, CheckCircle, XCircle, Camera,
  DollarSign, QrCode, ArrowRight, AlertCircle, ClipboardCheck
} from 'lucide-react';
import './SponsorPages.css';

/**
 * v2 Sponsor Scan Page — Mandatory Value Entry Flow
 *
 * Flow: Enter code → Validate → Enter bill value → Calculate discount → Review & Confirm → Done
 *
 * The sponsor MUST enter the bill total before the discount is revealed.
 * This ensures every redemption captures the full transaction value.
 */
const SponsorScanPage = () => {
  const { clubSlug } = useParams();
  const location = useLocation(); // UC-B5: return path for /login
  const { user, loading: authLoading, getMembership } = useAuth();
  const membership = getMembership(clubSlug);

  // Flow state: 'entry' → 'value_entry' → 'confirm' → 'success'
  const [step, setStep] = useState('entry');

  const [codeInput, setCodeInput] = useState('');
  const [billValue, setBillValue] = useState('');
  const [validating, setValidating] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [validatedCode, setValidatedCode] = useState(null);
  const [calculation, setCalculation] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Camera scanning (html5-qrcode) — the primary path for sponsors using
  // their phone browser at the till. Manual entry stays as the fallback.
  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const scannerRef = useRef(null);
  const decodeHandledRef = useRef(false);

  const stopScanner = useCallback(async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    setScanning(false);
    if (s) {
      try { await s.stop(); s.clear(); } catch { /* already stopped */ }
    }
  }, []);

  // Kill the camera if the component unmounts mid-scan.
  useEffect(() => () => { stopScanner(); }, [stopScanner]);

  const { data: club } = useQuery({
    queryKey: ['club', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}`).then(r => r.data),
  });

  // The logged-in sponsor's own record (business name + logo) for the header.
  const { data: me } = useQuery({
    queryKey: ['sponsor-me', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}/sponsors/me`).then(r => r.data),
    enabled: !!membership,
  });

  if (authLoading) return <div className="page-loading"><div className="spinner" style={{ width: 40, height: 40 }} /></div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (membership && membership.role !== 'sponsor' && membership.role !== 'admin') {
    return <Navigate to={`/${clubSlug}`} replace />;
  }

  // Step 1: Validate the code. `raw` may be the typed short code OR the full
  // JSON payload decoded from the QR — the backend accepts both.
  const handleValidate = async (raw) => {
    const codeToSend = (raw ?? codeInput).trim();
    if (!codeToSend) return;
    setError(''); setValidating(true);
    try {
      const res = await axios.post(`/clubs/${clubSlug}/sponsors/scan/validate`, { code: codeToSend });
      if (res.data.valid) {
        setValidatedCode(res.data);
        setStep('value_entry');
      } else {
        setError(res.data.error || 'Code not valid');
      }
    } catch (err) { setError(err.response?.data?.error || 'Failed to validate code'); }
    finally { setValidating(false); }
  };

  const startScanner = () => {
    setError(''); setCameraError('');
    decodeHandledRef.current = false;
    setScanning(true);
    // Wait a tick so the viewfinder div exists before the library grabs it.
    setTimeout(async () => {
      try {
        const scanner = new Html5Qrcode('qr-viewfinder');
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 230, height: 230 } },
          async (decodedText) => {
            if (decodeHandledRef.current) return; // library can fire repeatedly
            decodeHandledRef.current = true;
            await stopScanner();
            // Show the short code in the input for visual confirmation.
            let display = decodedText;
            try { const p = JSON.parse(decodedText); if (p.code) display = p.code; } catch { /* raw code */ }
            setCodeInput(display);
            handleValidate(decodedText);
          },
          () => { /* per-frame decode misses — normal, ignore */ }
        );
      } catch {
        scannerRef.current = null;
        setScanning(false);
        setCameraError('Camera not available — check the browser has camera permission, or type the code below.');
      }
    }, 60);
  };

  // Step 2: Calculate the discount
  const handleCalculate = async () => {
    if (!billValue || parseFloat(billValue) <= 0) {
      setError('Please enter a valid bill amount');
      return;
    }
    setError(''); setCalculating(true);
    try {
      const res = await axios.post(`/clubs/${clubSlug}/sponsors/scan/calculate`, {
        code: validatedCode.code,
        transactionValue: parseFloat(billValue),
      });
      setCalculation(res.data);
      setStep('confirm');
    } catch (err) { setError(err.response?.data?.error || 'Failed to calculate discount'); }
    finally { setCalculating(false); }
  };

  // Step 3: Confirm & redeem
  const handleRedeem = async () => {
    if (!calculation) return;
    setRedeeming(true); setError('');
    try {
      // Request geolocation if available
      let latitude = null, longitude = null;
      try {
        if (navigator.geolocation) {
          const pos = await new Promise((resolve, reject) =>
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 3000, maximumAge: 60000 })
          );
          latitude = pos.coords.latitude;
          longitude = pos.coords.longitude;
        }
      } catch { /* geo not available, continue without */ }

      const res = await axios.post(`/clubs/${clubSlug}/sponsors/scan/redeem`, {
        code: validatedCode.code,
        transactionValue: calculation.transaction_value,
        latitude,
        longitude,
      });
      setResult(res.data);
      setStep('success');
    } catch (err) { setError(err.response?.data?.error || 'Failed to redeem code'); }
    finally { setRedeeming(false); }
  };

  const resetAll = () => {
    setStep('entry');
    setCodeInput(''); setBillValue('');
    setValidatedCode(null); setCalculation(null);
    setResult(null); setError('');
  };

  // Go back one step
  const goBack = () => {
    setError('');
    if (step === 'confirm') { setCalculation(null); setStep('value_entry'); }
    else if (step === 'value_entry') { setValidatedCode(null); setBillValue(''); setStep('entry'); }
  };

  // W-2: an unverified club has no sponsors to scan for — its admin gets the
  // revenue case + a link to the verification form, not a dead scanner.
  if (club && !club.verified_at && membership?.role !== 'sponsor') {
    return (
      <div className="sponsor-page">
        <Navbar club={club} />
        <PageHeader icon={ScanLine} title="Scan & Redeem" subtitle={club.name} />
        <UnverifiedExplainer clubSlug={clubSlug} club={club} what="Scan & Redeem" />
      </div>
    );
  }

  return (
    <div className="sponsor-page">
      <Navbar club={club} />
      {/* Sponsor guided tour — /scan is a sponsor's home surface. Slice 2. */}
      <GuidedTour club={club} surface="scan" />

      <PageHeader
        icon={ScanLine}
        logoUrl={me?.logo_url || undefined}
        title="Scan & Redeem"
        subtitle={<>{me?.business_name || `${user.firstName} ${user.lastName}`} · {club?.name || clubSlug}</>}
      />

      <div className="sponsor-content scan-content">

        {/* ── STEP 4: Success ── */}
        {step === 'success' && result && result.valid && (
          <div className="scan-result scan-success card">
            <CheckCircle size={56} style={{ color: 'var(--success)' }} />
            <h2 style={{ color: 'var(--success)' }}>Redemption Complete!</h2>
            <div className="scan-result-details">
              <div className="scan-detail-row"><span>Member</span><strong>{result.member_name || 'Club member'}</strong></div>
              <div className="scan-detail-row"><span>Club</span><strong>{result.club_name}</strong></div>
              <div className="scan-detail-row"><span>Offer</span><strong>{result.offer_title}</strong></div>
              <div className="scan-detail-row" style={{ borderTop: '2px solid var(--border)', paddingTop: 12, marginTop: 8 }}>
                <span>Bill Total</span>
                <strong>£{parseFloat(result.transaction_value).toFixed(2)}</strong>
              </div>
              <div className="scan-detail-row">
                <span>Discount Applied</span>
                <strong style={{ color: 'var(--success)' }}>
                  −£{parseFloat(result.discount_value).toFixed(2)}
                  <span style={{ fontSize: 12, fontWeight: 400, marginLeft: 4, color: 'var(--text-secondary)' }}>
                    ({result.discount_type === 'fixed' ? 'flat' : `${result.discount_rate}%`})
                  </span>
                </strong>
              </div>
              <div className="scan-detail-row" style={{ borderTop: '2px solid var(--green)', paddingTop: 12, marginTop: 8, background: 'rgba(45,122,79,0.04)', borderRadius: 8, padding: '12px 8px' }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>Customer Pays</span>
                <strong style={{ fontSize: 22, color: 'var(--green)' }}>£{parseFloat(result.net_value).toFixed(2)}</strong>
              </div>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 12 }}>
              Apply £{parseFloat(result.discount_value).toFixed(2)} discount at your till. This transaction has been recorded.
            </p>
            <button className="btn btn-primary" onClick={resetAll} style={{ marginTop: 20, width: '100%', justifyContent: 'center' }}>
              <ScanLine size={15} /> Scan another code
            </button>
          </div>
        )}

        {/* ── STEP 3: Confirm & Redeem ── */}
        {step === 'confirm' && calculation && (
          <div className="scan-result scan-preview card">
            <ClipboardCheck size={40} style={{ color: 'var(--green)' }} />
            <h2>Confirm Redemption</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginTop: -8 }}>
              Review the discount below, then confirm to complete.
            </p>
            <div className="scan-result-details">
              <div className="scan-detail-row"><span>Member</span><strong>{calculation.member_name || 'Club member'}</strong></div>
              <div className="scan-detail-row"><span>Offer</span><strong>{calculation.offer_title}</strong></div>
              <div className="scan-detail-row" style={{ borderTop: '2px solid var(--border)', paddingTop: 12, marginTop: 8 }}>
                <span>Bill Total</span>
                <strong>£{parseFloat(calculation.transaction_value).toFixed(2)}</strong>
              </div>
              <div className="scan-detail-row">
                <span>Discount ({calculation.discount_type === 'fixed' ? `£${calculation.discount_rate} off` : `${calculation.discount_rate}%`})</span>
                <strong style={{ color: 'var(--success)' }}>−£{parseFloat(calculation.discount_value).toFixed(2)}</strong>
              </div>
              {calculation.max_discount_applied && (
                <div className="scan-detail-row" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  <span><AlertCircle size={12} /> Maximum discount cap applied</span>
                </div>
              )}
              <div className="scan-detail-row" style={{ borderTop: '2px solid var(--green)', paddingTop: 12, marginTop: 8, background: 'rgba(45,122,79,0.04)', borderRadius: 8, padding: '12px 8px' }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>Customer Pays</span>
                <strong style={{ fontSize: 22, color: 'var(--green)' }}>£{parseFloat(calculation.net_value).toFixed(2)}</strong>
              </div>
            </div>
            {error && <div className="scan-error" style={{ marginTop: 12 }}><XCircle size={16} /> {error}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button className="btn btn-primary" onClick={handleRedeem} disabled={redeeming} style={{ flex: 1, justifyContent: 'center' }}>
                {redeeming ? 'Processing...' : <><CheckCircle size={15} /> Confirm Redemption</>}
              </button>
              <button className="btn btn-ghost" onClick={goBack}>Back</button>
            </div>
          </div>
        )}

        {/* ── STEP 2: Enter Bill Value ── */}
        {step === 'value_entry' && validatedCode && (
          <div className="scan-result scan-preview card">
            <QrCode size={40} style={{ color: 'var(--green)' }} />
            <h2>Valid Code — Enter Bill Total</h2>
            <div className="scan-result-details">
              <div className="scan-detail-row"><span>Member</span><strong>{validatedCode.member_name || 'Club member'}</strong></div>
              <div className="scan-detail-row"><span>Club</span><strong>{validatedCode.club_name}</strong></div>
              <div className="scan-detail-row"><span>Offer</span><strong>{validatedCode.offer_title}</strong></div>
              {validatedCode.offer_value && <div className="scan-detail-row"><span>Discount</span><strong style={{ color: 'var(--green)' }}>{validatedCode.offer_value}</strong></div>}
            </div>
            {validatedCode.offer_description && <p className="scan-offer-desc">{validatedCode.offer_description}</p>}

            <div className="form-group" style={{ marginTop: 20 }}>
              <label className="label" style={{ fontWeight: 700, fontSize: 15 }}>
                <DollarSign size={14} /> Enter the total bill amount before discount
              </label>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0 8px' }}>
                This is required to calculate the discount. The discount amount will be shown on the next screen.
              </p>
              <input
                className="input"
                type="number"
                step="0.01"
                min="0"
                placeholder="e.g. 42.50"
                value={billValue}
                onChange={e => setBillValue(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCalculate()}
                autoFocus
                style={{ fontSize: 20, textAlign: 'center', fontWeight: 700, padding: '14px 16px' }}
              />
              {validatedCode.min_spend && (
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                  Minimum spend: £{parseFloat(validatedCode.min_spend).toFixed(2)}
                </p>
              )}
            </div>

            {error && <div className="scan-error" style={{ marginTop: 8 }}><XCircle size={16} /> {error}</div>}

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="btn btn-primary" onClick={handleCalculate}
                disabled={!billValue || parseFloat(billValue) <= 0 || calculating}
                style={{ flex: 1, justifyContent: 'center' }}>
                {calculating ? 'Calculating...' : <><ArrowRight size={15} /> Calculate Discount</>}
              </button>
              <button className="btn btn-ghost" onClick={goBack}>Back</button>
            </div>
          </div>
        )}

        {/* ── STEP 1: Scan or enter code ── */}
        {step === 'entry' && (
          <div className="scan-entry">
            <div className="scan-entry-card card">
              {!scanning && (
                <>
                  <div className="scan-entry-icon">
                    <Camera size={32} style={{ color: 'var(--green)' }} />
                  </div>
                  <h2>Scan Member Code</h2>
                  <p>Ask the member to show their QR code, then point your camera at it.</p>
                  <button className="btn btn-primary" onClick={startScanner}
                    style={{ width: '100%', justifyContent: 'center', marginTop: 16, padding: '14px 20px', fontSize: 16 }}>
                    <Camera size={18} /> Scan QR with camera
                  </button>
                  {cameraError && (
                    <div className="scan-error" style={{ marginTop: 10 }}><AlertCircle size={16} /> {cameraError}</div>
                  )}
                </>
              )}

              {scanning && (
                <>
                  <h2 style={{ marginBottom: 6 }}>Point at the QR code</h2>
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
                    It validates automatically the moment it's read.
                  </p>
                  <div id="qr-viewfinder" style={{ width: '100%', borderRadius: 12, overflow: 'hidden', background: '#000' }} />
                  <button className="btn btn-ghost" onClick={stopScanner} style={{ width: '100%', justifyContent: 'center', marginTop: 10 }}>
                    Cancel camera
                  </button>
                </>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0 4px' }}>
                <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                <span style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>or type the code</span>
                <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              </div>
              <div className="form-group" style={{ marginTop: 10 }}>
                <input className="input scan-code-input" placeholder="MRK-XXXXXXXX"
                  value={codeInput}
                  onChange={e => setCodeInput(e.target.value.toUpperCase())}
                  onKeyDown={e => e.key === 'Enter' && handleValidate()} />
              </div>
              {error && <div className="scan-error"><XCircle size={16} /> {error}</div>}
              <button className="btn btn-secondary" onClick={() => handleValidate()}
                disabled={!codeInput.trim() || validating}
                style={{ width: '100%', justifyContent: 'center', marginTop: 10 }}>
                {validating ? 'Checking...' : <><ScanLine size={15} /> Validate Code</>}
              </button>
            </div>

            <div className="scan-help card">
              <h3>How it works</h3>
              <div className="scan-help-steps">
                {[
                  'Member shows their QR code from the Marakin app',
                  'Scan it with your camera (or type the MRK- code under it)',
                  'Enter the customer\'s bill total before any discount',
                  'Review the calculated discount and confirm redemption',
                  'Apply the discount at your till — the transaction is recorded automatically',
                ].map((text, i) => (
                  <div className="scan-help-step" key={i}>
                    <span className="step-num">{i + 1}</span>
                    <p>{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SponsorScanPage;
